import { Component, Inject, inject } from '@angular/core';
import { AbstractStepDTO, ConfiguratorLandingPageService, LandingPageEventStepDTO } from '@tj/tj-dmp-lib-api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import { ON_CANCEL_TOKEN, ON_SAVE_TOKEN, PARTNER_TOKEN, READ_ONLY_MODE_TOKEN, STEP_TOKEN, TENANT_TOKEN } from '../injection-tokens';
import { StepDescriptionService } from '../step-description.service';
import { landingPageOptions } from '../../../shared/landing-page-link.util';

@Component({
    selector: 'dmp-landing-page-event-step-config',
    templateUrl: './landing-page-event-step-config.component.html',
    standalone: false
})
export class LandingPageEventStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);

  landingPageForm: FormGroup;
  availableLandingPages: { label: string; value: string }[] = [];

  get landingPageEventStep(): LandingPageEventStepDTO {
    return this.step as LandingPageEventStepDTO;
  }

  get idLandingPage() { 
    return this.landingPageForm.get('idLandingPage'); 
  }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private landingPageService: ConfiguratorLandingPageService,
    private formBuilder: FormBuilder
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.loadLandingPages();
  }

  protected updateStepFromForm(): void {
    if (!this.landingPageEventStep || !this.landingPageForm.valid) return;

    const idLandingPage = this.idLandingPage.value;
    this.landingPageEventStep.description = this.generateReadableDescription(idLandingPage);
    this.landingPageEventStep.idLandingPage = idLandingPage;
  }

  protected isConfigurationValid(): boolean {
    return this.landingPageForm.valid;
  }

  private createForm(): void {
    this.landingPageForm = this.formBuilder.group({
      idLandingPage: this.createFormControl('', [Validators.required])
    });

    this.landingPageForm.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.markAsChanged();
      }
    });
  }

  private loadLandingPages(): void {
    if (!this.tenant) return;

    this.landingPageService.getAllLandingPagesUsingGET(this.tenant, 0, 100, undefined, undefined, true).subscribe({
      next: landingPages => {
        const labelByCode = new Map(landingPageOptions(landingPages).map(option => [option.value, option.label]));
        this.availableLandingPages = landingPages
          .filter(landingPage => labelByCode.has(landingPage.code))
          .map(landingPage => ({ label: labelByCode.get(landingPage.code), value: landingPage.id }))
          .sort((a, b) => a.label.localeCompare(b.label));

        this.initializeForm();
      },
      error: error => console.error('Erreur chargement landing pages:', error)
    });
  }

  private initializeForm(): void {
    if (!this.landingPageEventStep.idLandingPage) return;

    this.isInitializing = true;

    this.landingPageForm.patchValue({
      idLandingPage: this.landingPageEventStep.idLandingPage
    });

    this.isInitializing = false;
  }

  private generateReadableDescription(idLandingPage: string): string {
    const template = this.availableLandingPages.find(lp => lp.value === idLandingPage);
    return this.stepDescriptionService.landingPageEvent(template?.label || idLandingPage);
  }
}